// SponsorLayout — the sponsor shell. A sibling of AppLayout rather than a variant of
// it: AppLayout mounts participant chrome, so there is nothing to parameterise. What the
// two DO share is reused rather than forked — RisaProvider + RisaPanel, the
// CommandPalette, the trust Footer, and the app-shell layout CSS.
//
// HIDDEN (matching FM-2406 on the participant side): the sponsor top bar is gone. Its
// only real control was the avatar, which now rides in each page's head row beside
// "Ask Risa" as `components/sponsor-header/SponsorAccountMenu` — the same shape the
// participant pages use. `SponsorHeader.jsx` is left in place, unmounted: uncomment the
// import and the element below to restore it. The back-to-plans affordance it carried is
// now the page-head breadcrumb, and its plan name is in the page's own metadata strip.
import { useCallback, useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Footer from '../footer/Footer';
// import SponsorHeader from '../sponsor-header/SponsorHeader';
import { CommandPalette, Spinner } from '../reusable';
import { RisaProvider, RisaPanel, useRisa } from '../modules/risa';
import { useLanguage } from '../../hooks';
import { SEARCH_GROUPS } from '../../config/searchCatalog';
import '../../styles/app-layout.css';

function SponsorShell() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { switching } = useLanguage();
  const [searchOpen, setSearchOpen] = useState(false);
  const { isOpen: risaOpen, context: risaContext, close: closeRisa, toggle: toggleRisa } = useRisa();

  // Same shortcuts as the participant shell: ⌘K search, ⌘G Risa. With the top bar gone
  // the keyboard is the only way into search, exactly as on the participant side.
  useEffect(() => {
    const onKey = (e) => {
      const k = e.key.toLowerCase();
      if ((e.metaKey || e.ctrlKey) && k === 'k') {
        e.preventDefault();
        setSearchOpen((v) => !v);
      } else if ((e.metaKey || e.ctrlKey) && k === 'g') {
        e.preventDefault();
        toggleRisa();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggleRisa]);

  const handleSelect = useCallback(
    (item) => {
      setSearchOpen(false);
      if (item?.path) navigate(item.path);
    },
    [navigate],
  );

  return (
    <div className="app-shell">
      <main className={'app-shell__main' + (risaOpen ? ' is-risa-open' : '')}>
        <div className="app-shell__page">
          <Outlet />
        </div>

        {switching && (
          <div className="app-shell__lang-overlay" role="status" aria-live="polite">
            <div className="app-shell__lang-card">
              <Spinner size="lg" label={t('footer.switchingLanguage')} />
              <span className="app-shell__lang-text">{t('footer.switchingLanguage')}</span>
            </div>
          </div>
        )}
      </main>
      <Footer />

      {risaOpen && <RisaPanel onClose={closeRisa} context={risaContext} />}

      <CommandPalette
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onSelect={handleSelect}
        groups={SEARCH_GROUPS}
      />
    </div>
  );
}

function SponsorLayout() {
  return (
    <RisaProvider>
      <SponsorShell />
    </RisaProvider>
  );
}

export default SponsorLayout;
